'use client';

import { useFeatureFlag } from '@/@feature-flags/hooks';
import { ChatInput } from '@/@studio-ai/components/chat-input';
import { ChatMessages } from '@/@studio-ai/components/chat-messages';
import { StudioAICreditBanners } from '@/@studio-ai/components/studio-ai-credit-banners';
import { ShareModal } from '@/@studio-ai/components/history/share-modal';
import { RefreshDataDates } from '@/@studio-ai/components/refresh-data-dates';
import { ScrollToTopButton } from '@/@studio-ai/components/scroll-to-top-button';
import { STUDIO_AI_NAME, AgentModes } from '@/@studio-ai/constants';
import { useStudioAI } from '@/@studio-ai/context';
import { useOrgTrialUsageLimit } from '@/@studio-ai/hooks/useOrgTrialUsageLimit';
import { useStudioCreditAlerts } from '@/@studio-ai/hooks/useStudioCreditAlerts';
import { ASSISTANT_CHAT_INPUT_MESSAGES } from '@/@studio-ai/persistent-assistant/constants';
import { ChatPanelProps } from '@/@studio-ai/persistent-assistant/props';
import type { ChatInputSendOptions } from '@/@studio-ai/props';
import { generateConversationId } from '@/@studio-ai/utils/message-utils';
import { Button, cn, Image, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import NextImage from 'next/image';
import { useParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePanelResize } from '../hooks/use-panel-resize';
import { getPageContext } from '../utils/page-context-registry';
import { ConversationHistoryPopover } from './conversation-history-popover';

export function ChatPanel({
    onClose,
    hideExtraOptions = false,
    selectedText,
    initialMessage,
    onInitialMessageSent,
    onHasMessagesChange,
    onSelectedTextClear,
    onBeforeSend,
    resizable = false,
    resizeStorageKey = 'chat-panel-width',
    defaultWidth = 380,
    minWidth = 320,
    maxWidth = 800,
    isMaximized = false,
    onMaximizeToggle,
    isSharedView = false,
}: ChatPanelProps) {
    const {
        currentConversationId,
        messages,
        sendMessage,
        stopConversation,
        isLoading,
        reset,
        dataRefreshDates,
        userSession,
        isCurrentConversationOwner,
        organizationUsers,
        shareConversation,
        getConversationShares,
        revokeShare,
        effectiveAgentMode,
        smartRoutingEnabled,
        setSmartRoutingEnabled,
        setAgentMode,
    } = useStudioAI();

    const creditAlerts = useStudioCreditAlerts();

    const isPlannerFlagEnabled = useFeatureFlag('studio-ai-planner-mode');

    const handleSmartRoutingToggle = useCallback(
        (enabled: boolean | ((previous: boolean) => boolean)) => {
            if (typeof enabled === 'function') {
                setSmartRoutingEnabled((prev) => {
                    const next = enabled(prev);
                    if (isPlannerFlagEnabled) {
                        setAgentMode(next ? AgentModes.PLANNER : null);
                    }
                    return next;
                });
            } else {
                setSmartRoutingEnabled(enabled);
                if (isPlannerFlagEnabled) {
                    setAgentMode(enabled ? AgentModes.PLANNER : null);
                }
            }
        },
        [isPlannerFlagEnabled, setAgentMode, setSmartRoutingEnabled],
    );

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const isUnaffiliated = userSession?.user?.isUnaffiliated ?? false;
    const isFreemium = isUnaffiliated;
    const usageLimit = useOrgTrialUsageLimit();

    const {
        width: panelWidth,
        handlePointerDown: handleResizePointerDown,
        isDragging,
    } = usePanelResize({ minWidth, maxWidth, defaultWidth, storageKey: resizeStorageKey });

    const pathname = usePathname();
    const params = useParams();
    const initialMessageSentRef = useRef(false);

    useEffect(() => {
        onHasMessagesChange?.(messages.length > 0);
    }, [messages.length, onHasMessagesChange]);

    const placeholder = useMemo(() => {
        if (messages.length > 0) return ASSISTANT_CHAT_INPUT_MESSAGES.FOLLOW_UP_PLACEHOLDER;

        const pageMatch = Object.entries(ASSISTANT_CHAT_INPUT_MESSAGES.PAGE_PLACEHOLDERS).find(
            ([route]) => pathname === route || pathname.startsWith(`${route}/`),
        );
        if (pageMatch) return pageMatch[1];

        return ASSISTANT_CHAT_INPUT_MESSAGES.DEFAULT_PLACEHOLDER;
    }, [messages.length, pathname, selectedText]);

    const scrollToBottomRef = useRef<(() => void) | null>(null);
    const scrollToTopRef = useRef<(() => void) | null>(null);
    const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
    const [showScrollToTopButton, setShowScrollToTopButton] = useState(false);

    const handleSend = async (content: string, sendOptions?: ChatInputSendOptions) => {
        const conversationId = currentConversationId || generateConversationId();
        const selectionContext = selectedText || null;

        const registeredPageData = getPageContext();
        const hasRouteParams = Object.keys(params).length > 0;
        const hasRegisteredData = Object.keys(registeredPageData).length > 0;
        const effectiveSmartRoutingEnabled =
            sendOptions?.smartRoutingEnabled ?? smartRoutingEnabled;

        const contextData: Record<string, unknown> = {
            displayMessage: content,
            pagePath: pathname,
            smartRoutingEnabled: effectiveSmartRoutingEnabled,
            ...(selectionContext && { selectedText: selectionContext }),
            ...((hasRouteParams || hasRegisteredData) && {
                pageData: {
                    ...(hasRouteParams && { routeParams: params }),
                    ...registeredPageData,
                },
            }),
        };

        // Let the workspace intercept the message. If it returns true the send is
        // cancelled (e.g. the iOOH plan launcher takes over).
        const cancelled = await onBeforeSend?.(content);
        if (cancelled) return;

        try {
            await sendMessage({
                prompt: content,
                conversationId,
                parentMessageId: '0',
                agentMode: effectiveAgentMode,
                contextData,
            });
        } catch {
            // Error handling is done in the context
        }
    };

    useEffect(() => {
        if (initialMessage && !initialMessageSentRef.current) {
            initialMessageSentRef.current = true;
            void handleSend(initialMessage);
            onInitialMessageSent?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleScrollStateChange = useCallback((isAtTop: boolean, isAtBottom: boolean) => {
        setShowScrollToTopButton(!isAtTop);
        setShowScrollToBottomButton(!isAtBottom);
    }, []);

    const handleScrollToBottom = useCallback(() => {
        scrollToBottomRef.current?.();
    }, []);

    const handleScrollToTop = useCallback(() => {
        scrollToTopRef.current?.();
    }, []);

    const hasMessages = messages.length > 0;

    const content = (
        <div className="relative flex h-full w-full flex-col border-l border-default-200/60 bg-background dark:border-default-100">
            {!isSharedView &&
                currentConversationId &&
                isCurrentConversationOwner &&
                isShareModalOpen && (
                    <ShareModal
                        isOpen={isShareModalOpen}
                        onClose={() => setIsShareModalOpen(false)}
                        conversationId={currentConversationId}
                        conversationTitle="Conversation"
                        organizationUsers={organizationUsers}
                        onShare={shareConversation}
                        onGetShares={getConversationShares}
                        onRevokeShare={revokeShare}
                    />
                )}
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-default-200/60 px-4 py-3 dark:border-default-100">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center rounded-lg bg-mfour-green p-2 text-white">
                        <Icon icon="lucide:bot" width={24} height={24} />
                    </div>
                    <span className="text-small font-bold text-foreground">{STUDIO_AI_NAME}</span>
                </div>
                {!hideExtraOptions && (
                    <div className="flex items-center gap-1">
                        {hasMessages && (
                            <Tooltip content="New chat" size="sm" delay={400}>
                                <Button
                                    isIconOnly
                                    aria-label="New chat"
                                    variant="light"
                                    radius="full"
                                    size="sm"
                                    className="h-7 w-7 min-w-7 text-default-400 hover:text-default-600"
                                    onPress={reset}
                                >
                                    <Icon icon="lucide:plus" width={16} height={16} />
                                </Button>
                            </Tooltip>
                        )}
                        {!isSharedView &&
                            hasMessages &&
                            currentConversationId &&
                            isCurrentConversationOwner && (
                                <Tooltip content="Share conversation" size="sm" delay={400}>
                                    <Button
                                        isIconOnly
                                        aria-label="Share conversation"
                                        variant="light"
                                        radius="full"
                                        size="sm"
                                        className="h-7 w-7 min-w-7 text-default-400 hover:text-default-600"
                                        onPress={() => setIsShareModalOpen(true)}
                                    >
                                        <Icon icon="lucide:share-2" width={16} height={16} />
                                    </Button>
                                </Tooltip>
                            )}
                        <ConversationHistoryPopover
                            placement="bottom-end"
                            trigger={
                                <Button
                                    aria-label="Conversation history"
                                    size="sm"
                                    className="h-7 gap-1 bg-mfour-green px-2 text-white"
                                    startContent={
                                        <Icon icon="lucide:clock" width={14} height={14} />
                                    }
                                >
                                    <span className="text-tiny font-medium">History</span>
                                </Button>
                            }
                        />
                        {onMaximizeToggle && (
                            <Tooltip
                                content={isMaximized ? 'Restore' : 'Maximize'}
                                size="sm"
                                delay={400}
                            >
                                <Button
                                    isIconOnly
                                    aria-label={isMaximized ? 'Restore panel' : 'Maximize panel'}
                                    variant="light"
                                    radius="full"
                                    size="sm"
                                    className="h-7 w-7 min-w-7 text-default-400 hover:text-default-600"
                                    onPress={onMaximizeToggle}
                                >
                                    <Icon
                                        icon={
                                            isMaximized ? 'lucide:minimize-2' : 'lucide:maximize-2'
                                        }
                                        width={15}
                                        height={15}
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip content="Close" size="sm" delay={400}>
                            <Button
                                isIconOnly
                                aria-label="Close"
                                variant="light"
                                radius="full"
                                size="sm"
                                className="h-7 w-7 min-w-7 text-default-400 hover:text-default-600"
                                onPress={onClose}
                            >
                                <Icon icon="lucide:x" width={16} height={16} />
                            </Button>
                        </Tooltip>
                    </div>
                )}
            </div>

            {/* Body */}
            {!hasMessages ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
                    <Image
                        src="/assets/images/MFourLogo.svg"
                        as={NextImage}
                        radius="none"
                        alt={STUDIO_AI_NAME}
                        width={48}
                        height={48}
                        className="opacity-80"
                    />
                    <div className="text-center">
                        <p className="text-small font-medium text-foreground">
                            {ASSISTANT_CHAT_INPUT_MESSAGES.MAIN}
                        </p>
                        <p className="mt-1 text-tiny text-default-400">
                            {ASSISTANT_CHAT_INPUT_MESSAGES.MAIN_SUBTITLE}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    <ChatMessages
                        messages={messages}
                        onScrollStateChange={handleScrollStateChange}
                        scrollToBottomRef={scrollToBottomRef}
                        constrainWidth={false}
                        scrollToTopRef={scrollToTopRef}
                        className="overflow-x-hidden"
                    />

                    {/* Scroll to bottom is not defined here because it should be bound to the chat input when it resizes */}
                    <ScrollToTopButton show={showScrollToTopButton} onPress={handleScrollToTop} />
                </div>
            )}

            {/* Selected text context chip — reactive to live browser selection */}
            <AnimatePresence>
                {selectedText && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                        className="shrink-0 overflow-hidden border-t border-default-200/60 px-4 pt-2.5 dark:border-default-100"
                    >
                        <div className="flex max-w-full items-start gap-1.5 rounded-lg bg-mfour-green/10 px-2.5 py-2 dark:bg-mfour-green/30">
                            <Icon
                                icon="lucide:text-select"
                                width={14}
                                height={14}
                                className="mt-0.5 shrink-0 text-mfour-green dark:text-mfour-green/40"
                            />
                            <p className="line-clamp-3 min-w-0 flex-1 text-mini font-medium leading-snug text-mfour-green dark:text-mfour-green/30">
                                {selectedText}
                            </p>
                            <button
                                type="button"
                                onClick={onSelectedTextClear}
                                className="ml-1 shrink-0 rounded-full p-0.5 text-green-500 transition-colors hover:bg-green-200/60 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-800/40 dark:hover:text-green-200"
                                aria-label="Remove selected text"
                            >
                                <Icon icon="lucide:x" width={12} height={12} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Input */}
            {isSharedView ? (
                <div className="shrink-0 border-t border-divider bg-secondary-50 p-3">
                    <div className="flex items-center justify-center gap-1 text-small text-secondary-600">
                        <Icon icon="lucide:eye" width={16} />
                        <span className="text-sm text-secondary-600">
                            You are viewing a shared conversation (read-only)
                        </span>
                    </div>
                </div>
            ) : (
                <div className="shrink-0 space-y-2 border-t border-default-200/60 px-4 py-3 dark:border-default-100">
                    <StudioAICreditBanners compact />
                    <ChatInput
                        conversationId={currentConversationId}
                        onSendMessage={handleSend}
                        isLoading={isLoading}
                        placeholder={
                            creditAlerts.isPaused
                                ? (creditAlerts.pausePlaceholder ?? placeholder)
                                : placeholder
                        }
                        showScrollButton={showScrollToBottomButton}
                        onScrollToBottom={handleScrollToBottom}
                        handleStop={stopConversation}
                        showSmartRoutingToggle={isPlannerFlagEnabled}
                        smartRoutingEnabled={smartRoutingEnabled}
                        setSmartRoutingEnabled={handleSmartRoutingToggle}
                        usageLimit={usageLimit}
                        trialStrip={creditAlerts.trialStrip}
                        isCreditGatePaused={creditAlerts.isPaused}
                        compact
                    />
                    {dataRefreshDates && !isFreemium && (
                        <RefreshDataDates dataRefreshDates={dataRefreshDates} />
                    )}
                </div>
            )}
        </div>
    );

    if (!resizable) return content;

    if (isMaximized) {
        return <div className="relative h-full w-full shrink-0">{content}</div>;
    }

    return (
        <div className="relative h-full shrink-0" style={{ width: panelWidth }}>
            <div
                className={cn(
                    'absolute left-0 top-0 z-10 h-full w-1.5 cursor-col-resize transition-colors hover:bg-primary/20',
                    isDragging && 'bg-primary/30',
                )}
                onPointerDown={handleResizePointerDown}
            />
            {content}
        </div>
    );
}
